import { useMemo } from 'react';
import { additionalOptionsArray } from '../../AiConfigs/constants';
import KitModal from '../../UI/KitModal';
import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { useAuthStore } from '../../../store/storeAuth';
import useFlowsStore from '../../../store/storeFlows';

type Props = {
  onHide: () => void;
  show: boolean;
  formData: any;
  onChange: (e: any) => void;
};

type TechModel = {
  type: string;
  isDeprecated?: boolean;
  model: string;
  displayName?: string;
  disabledByCompany?: boolean;
};

type TechSystem = {
  system: string;
  models?: TechModel[];
};

const SettingsModal = ({ onHide, show, formData: values, onChange }: Props) => {
  const { technologies, companySpaceTechnologies } = useAuthStore(state => state);
  const { spaceId } = useFlowsStore();

  const models = useMemo(() => {
    const systemsToUse: TechSystem[] =
      spaceId && companySpaceTechnologies.length > 0
        ? companySpaceTechnologies
        : technologies;

    const openAiSystem = systemsToUse?.find(s => s.system === 'openAi');

    return (
      openAiSystem?.models
        ?.filter(m => m.type === 'Text' && !m.isDeprecated)
        ?.map(m => ({
          value: m.model,
          label: m.displayName || m.model,
          disabled: m.disabledByCompany ?? false,
        })) ?? []
    );
  }, [technologies, companySpaceTechnologies, spaceId]);

  const additionalOptionsArrayFiltered = useMemo(() => {
    return additionalOptionsArray.map(option => ({
      ...option,
      disabled: option.value !== 'OpenAI',
    }));
  }, []);

  return (
    <KitModal
      fullWidth
      sx={{
        '& .MuiPaper-root': {
          maxWidth: '320px',
          borderRadius: '16px',
        },
        '& .MuiDialogContent-root': {
          padding: '16px',
        },
        '& .MuiDialogActions-root': {
          padding: '16px',
        },
      }}
      showCloseButton={false}
      onHide={onHide}
      show={show}
    >
      <Typography variant='h6'> Settings</Typography>

      <FormControl
        sx={{
          mt: '24px',
          mb: '24px',
        }}
        fullWidth
      >
        <InputLabel htmlFor={'searchType'}>Search Type</InputLabel>
        <Select
          fullWidth
          value={values.searchType}
          name='searchType'
          className='nodrag'
          label='Search Type'
          onChange={(e: any) => onChange(e)}
        >
          <MenuItem value='similarity'>Similarity Search</MenuItem>
          <MenuItem value='similarity-ner'>Similarity Search (NER)</MenuItem>
        </Select>
      </FormControl>

      <FormControl
        fullWidth
        sx={{
          mb: '24px',
        }}
      >
        <InputLabel htmlFor='systemAi'>System </InputLabel>

        <Select
          name='systemAi'
          className='nodrag'
          label='System '
          fullWidth
          value={values.systemAi}
          onChange={e => onChange(e)}
        >
          {additionalOptionsArrayFiltered.map(opt => (
            <MenuItem key={opt.value} value={opt.value} disabled={opt.disabled}>
              {opt.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <FormControl fullWidth>
        <InputLabel htmlFor='model'>Ai Model</InputLabel>
        <Select
          className='nodrag'
          value={values.model}
          name='model'
          label='Ai Model'
          onChange={e => onChange(e)}
        >
          {models.map(opt => (
            <MenuItem key={opt.value} value={opt.value} disabled={opt.disabled}>
              {opt.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <TextField
        type='number'
        onChange={e => onChange(e)}
        value={values.topK}
        label='Top K'
        name='topK'
        sx={{
          mt: '24px',
        }}
        fullWidth
      />
    </KitModal>
  );
};

export default SettingsModal;
